<script lang="ts">
  import { afterUpdate, createEventDispatcher, onMount } from 'svelte';

  import { getAppConfig } from '../../../../common/config/app-config';
  import { getStr, isMobileWidth } from '../../../../common/util';
  import ExtraSwiper from '../../../common/components/ExtraSwiper.svelte';
  import PDPSectionCard from '../PDPSectionCard.svelte';
  import ProductColorVariantSlideTemplate from './ProductColorVariantSlideTemplate.svelte';
  import ProductSpecSlideTemplate from './ProductSpecSlideTemplate.svelte';

  let selectedCode;

  export let productVariantGroups;
  export let productData: any = {};
  export let code;
  export let preorder: boolean = false;
  const { encodedContextPath } = getAppConfig();

  $: isReStockable = productData?.isReStockable;
  $: isoutOfStock = productData?.stock?.stockLevelStatus?.code;
  afterUpdate(() => {
    selectedCode = code;
  });

  onMount(async () => {
    selectedCode = code;
  });

  let dispatcher = createEventDispatcher();

  async function handleSelectedData(data: any) {
    selectedCode = data.variantOption.code;
    if (preorder) {
      dispatcher('modelClick', data.variantOption.code);
      return false;
    }
    window.ACC.loader.showLoader();
    window.location.href = `${encodedContextPath}${data.variantOption.url}`;
  }
  $: hasMultipleVariants = productVariantGroups?.some(group => group?.variantValues?.length > 1);
  let IconUrl = getStr('extra.product.variants.iconUrl');
</script>

{#if hasMultipleVariants}
  <PDPSectionCard titleKey="extra.product.variant.title" icon={IconUrl} VariantSectionCard={true} {preorder}>
    {#each productVariantGroups as group}
      {#if group?.variantValues?.length > 1}
        <div class="same-sku-container" class:c_preorder-variant={preorder} class:c_restockable-variant={isReStockable}>
          <div class="font-weight-regular font-size-md storage-desc-title">{group.title}</div>
          {#if group.variantValues?.length}
            <section class="storage-desc-tag-container">
              <ExtraSwiper
                slidesData={group.variantValues.map(tag => ({
                  ...tag,
                  isReStockable: isReStockable && selectedCode === tag.variantOption.code,
                  isoutOfStock: isoutOfStock === 'outOfStock' && selectedCode === tag.variantOption.code,
                  selected: selectedCode === tag.variantOption.code,
                }))}
                slideTemplate={group.displayProductImage ? ProductColorVariantSlideTemplate : ProductSpecSlideTemplate}
                swiperOptions={{
                  slidesPerView: 'auto',
                  spaceBetween: isMobileWidth() ? 8 : 16,
                  navigation: group?.variantValues?.length > 3 ? true : false,
                  pagination: false,
                }}
                slideTemplateProps={{
                  selectedCode,
                  containerClass: `storage-desc-tag-container-inn ${group.displayProductImage ? ' c_colorvariant ' : ' c_storagevariant '}`,
                }}
                on:click={e => handleSelectedData(e.detail)}
              />
            </section>
          {:else}
            <p class="error-message">No options available.</p>
          {/if}
        </div>
      {/if}
    {/each}
  </PDPSectionCard>
{/if}

<style lang="scss">
  @import '../../../common/styles/colors.new.scss';
  .pdp-variants-section {
    background-color: $neutral-light-04;
    border-radius: 12px;
  }
  .same-sku-container {
    background: $primary-white;
    border-radius: 12px;
    margin-bottom: 12px;
    padding: 12px 12px 20px 12px;
    &:last-child {
      margin-bottom: 0;
    }

    &.c_preorder-variant {
      background-color: transparent;
      padding: 0;
      margin: 0 0 50px;
      @include desktop-max-width {
        margin: 0 0 22px;
      }
      .storage-desc-title {
        margin-bottom: 12px;
      }
    }
    :global(.mdc-card) {
      min-width: 130px;
      padding: 8px 12px 8px 6px;
    }
  }

  .storage-desc {
    margin-bottom: 48px;
    display: grid;

    &:last-child {
      margin-bottom: 0;
    }
    @include desktop-max-width {
      margin-bottom: 24px;
    }
    &-tag-container {
      display: flex;
      flex-direction: row;
      gap: 16px;
      padding: 0px 1px;
      overflow-x: auto;
      max-width: fit-content;

      @include desktop-max-width {
        gap: 12px;
      }
    }
    &-title {
      margin-bottom: 16px;
      height: 18px;
      color: $neutral-dark-01;
    }
  }

  .storage-desc-tag-container {
    scrollbar-width: none;
    :global {
      .extra-swiper-container .swiper-wrapper {
        padding-bottom: 2px;
      }
    }
    &::-webkit-scrollbar {
      display: none;
    }
    :global(.swiper-button-next),
    :global(.swiper-button-prev) {
      height: 24px !important;
      width: 24px !important;
      translate: 0 -15% !important;

      &::after {
        font-size: 12px !important;
      }
    }

    :global(.swiper-button-prev.swiper-button-disabled),
    :global(.swiper-button-next.swiper-button-disabled) {
      display: none;
    }

    :global(.storage-desc-tag-container-inn) {
      border-radius: 12px;
      border: 1px solid $secondary-gray-02;

      @include desktop-max-width {
        padding-bottom: 1px;
      }
    }

    :global(.storage-desc-tag-container-inn.selected:not(.c_oos-variant)) {
      border: 1px solid $icon-only-icon-blue;
    }
  }

  * :global(.mdc-card) {
    position: relative;
    min-width: 200px;
    border-radius: 12px;
    padding: 15px 12px;
    flex-direction: row;
    align-items: center;
    box-shadow: none;
    display: flex;
    background: $primary-white;
    gap: 8px;
    cursor: pointer;

    @include desktop-max-width {
      min-width: 160px;
      height: 72px;
      flex-direction: row;
      align-items: center;
      text-align: center;
    }
  }

  * :global(.smui-card__content) {
    padding: 0px;
    text-align: center;
    font-family: 'Dubai';
    font-size: 14px;
    font-weight: 500;
    line-height: 12px;
    letter-spacing: 0em;
    color: $neutral-dark-01;
  }

  .card-container {
    &-card-icon {
      margin: 0 8px 0 0;
      width: 64px;
      height: 64px;
      object-fit: fill;
      mix-blend-mode: normal;
      transform: matrix(-1, 0, 0, 1, 0, 0);
      @include desktop-max-width {
        padding: 0;
        width: 56px;
        height: 56px;
        margin: 0 8px 0 0;
      }
    }
  }
  :global {
    .preorder-variant-section {
      .same-sku-container {
        .mdc-card {
          min-width: 105px;
          @include desktop-max-width {
            height: 60px;
          }
          @include desktop-min-width {
            min-width: 150px;
          }
        }
      }
      .storage-desc-tag-container-inn {
        height: 100%;
        .smui-card__content {
          font-size: 14px;
          line-height: 20px;
          font-weight: 500;
          @include desktop-min-width {
            font-size: 16px;
          }
        }
        .tag.black {
          padding: 9px 2rem;
          min-width: 166px;
          align-items: flex-start;
          @include desktop-min-width {
            min-width: 211px;
          }
          .text {
            font-size: 14px;
            line-height: 20px;
            @include desktop-min-width {
              font-size: 18px;
              line-height: 16px;
            }
          }
        }
      }
    }
  }
</style>
